import { useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useMediaQuery, Box, Spinner, Center } from '@chakra-ui/react';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { ConcertResponse } from 'api/responseTypes';
import { useCartContext } from 'context/CartContext/CartContext';
import { useHoldTokenSessionTimerContext } from 'context/HoldTokenSessionTimerContext/HoldTokenSessionTimerContext';
import { useLanguageContext } from 'context/LanguageContext/LanguageContext';
import { useIsAuthenticated } from 'react-auth-kit';
import { useEffect, useState } from 'react';

import { ChartSeats, SessionInitialized } from 'types/seats';
import { FEATURE_FLAGS_ENABLED } from 'utils/constants';
import { useHoldToken } from '../hooks/useHoldToken';
import { toast } from 'utils/toast';
import { useSeatsioHandlers } from 'hooks/useSeatsioHandlers';

type HallProps = Pick<ConcertResponse, 'pricing'> & {
  event: string;
  eventId: number;
  currency: string;
  maxAllowedTickets: number;
  setIsValidSelection: (isValid: boolean) => void;
  onOpenModal?: () => void;
  requireAuthentication: boolean;
  disabled?: boolean;
};

export const Hall = ({
  pricing,
  event,
  eventId,
  currency,
  maxAllowedTickets,
  setIsValidSelection,
  onOpenModal,
  requireAuthentication,
  disabled = false,
}: HallProps) => {
  const { clearCart, setHoldToken } = useCartContext();
  const [isMobileWidth] = useMediaQuery('(max-width: 400px)');
  const { resetTimer } = useHoldTokenSessionTimerContext();
  const { language } = useLanguageContext();
  const holdToken = useHoldToken(clearCart);
  const intl = useIntl();
  const lastViolationRef = useRef<string>('');
  const isAuthenticated = useIsAuthenticated();
  const [mode, setMode] = useState<'normal' | 'static'>('static');

  const { handleSeatSelected, handleSeatRemoved, isDeselectingChildren } = useSeatsioHandlers({
    pricing,
    eventId,
    isAuthenticated,
    requireAuthentication,
    onUnauthenticated: onOpenModal,
  });

  // Rerender the chart mode to ensure that its working correctly
  useEffect(() => {
    if (!disabled) {
      setMode('normal');
    }
  }, [isAuthenticated, requireAuthentication, disabled]);

  useEffect(() => {
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildMessage = (violations: string[]) => {
    const orphan = violations.includes('noOrphanSeats');
    if (orphan) return intl.formatMessage({ id: 'seats.orphanError' });
    return intl.formatMessage({ id: 'seats.invalidSelection' });
  };

  return (
    <Box position='relative' width='100%' height='100%'>
      {disabled && (
        <Center position='absolute' inset={0} zIndex={2} bg='whiteAlpha.800' pointerEvents='all'>
          <Center
            dir='rtl'
            bg='orange.50'
            border='1px solid'
            borderColor='orange.300'
            borderRadius='lg'
            px={5}
            py={3}
          >
            <FormattedMessage
              id='eventpage.timeslots.selectFirst'
              defaultMessage='يجب اختيار التوقيت قبل اختيار المقعد'
            />
          </Center>
        </Center>
      )}
      {isDeselectingChildren && (
        <Center position='absolute' inset={0} zIndex={1} bg='whiteAlpha.700'>
          <Spinner size='xl' color='blue.500' thickness='4px' />
        </Center>
      )}
      <SeatsioSeatingChart
        width='100%'
        language={language === 'en' && FEATURE_FLAGS_ENABLED ? 'en' : 'ar'}
        workspaceKey={import.meta.env.VITE_APP_WORKSPACE_KEY}
        event={event}
        region='eu'
        maxSelectedObjects={maxAllowedTickets ? maxAllowedTickets : 4}
        pricing={pricing}
        session='manual'
        holdToken={holdToken}
        showFullScreenButton={isMobileWidth}
        onChartRendered={(chart: ChartSeats) => {
          setHoldToken(chart.holdToken);
        }}
        onSessionInitialized={(session: SessionInitialized) => {
          resetTimer(session.expiresInSeconds);
        }}
        selectionValidators={[
          { type: 'noOrphanSeats', mode: 'lenient', ignoreCategories: false, highlight: true },
        ]}
        onSelectionInvalid={async (violations: string[]) => {
          const currentViolation = violations.join(',');

          if (currentViolation !== lastViolationRef.current) {
            lastViolationRef.current = currentViolation;

            toast({
              title: intl.formatMessage({ id: 'seats.invalidSelection' }),
              description: buildMessage(violations),
              status: 'error',
              duration: 5000,
              isClosable: true,
              position: 'bottom-left',
            });
          }
          setIsValidSelection(false);
        }}
        onSelectionValid={() => {
          setIsValidSelection(true);
        }}
        onHoldTokenExpired={clearCart}
        onObjectSelected={handleSeatSelected}
        onObjectDeselected={handleSeatRemoved}
        onObjectClicked={() => {
          if (!isAuthenticated() && requireAuthentication && onOpenModal) {
            onOpenModal();
          }
        }}
        priceFormatter={(price: string) =>
          currency + ' ' + new Intl.NumberFormat().format(Number(price))
        }
        mode={mode}
      />
    </Box>
  );
};
